import { File, FolderOpen, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { formatBytes } from "@/lib/chart-helpers";

type MemoryFile = {
  name: string;
  path: string;
  size: number;
  modifiedAt: number;
};

export function MemoryBrowser() {
  const { rpc, state } = useGatewayRpc();
  const [files, setFiles] = useState<MemoryFile[]>([]);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFiles = useCallback(async () => {
    if (state !== "connected") {return;}
    setLoadingFiles(true);
    try {
      const res = await rpc<{ files: MemoryFile[] }>("metrics.memory.list");
      setFiles(res.files);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load files");
    } finally {
      setLoadingFiles(false);
    }
  }, [rpc, state]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const loadFile = async (filePath: string) => {
    setSelectedPath(filePath);
    setLoadingContent(true);
    setError(null);
    try {
      const res = await rpc<{ path: string; content: string }>("metrics.memory.read", { path: filePath });
      setContent(res.content);
      setOriginalContent(res.content);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to read file");
      setContent("");
      setOriginalContent("");
    } finally {
      setLoadingContent(false);
    }
  };

  const saveFile = async () => {
    if (!selectedPath) {return;}
    setSaving(true);
    setError(null);
    try {
      await rpc("metrics.memory.write", { path: selectedPath, content });
      setOriginalContent(content);
      void loadFiles(); // refresh file list for updated size/timestamp
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save file");
    } finally {
      setSaving(false);
    }
  };

  const isDirty = content !== originalContent;
  const selectedFile = files.find((f) => f.path === selectedPath);

  if (state !== "connected") {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Memory Browser</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Memory Browser</h1>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="grid h-[calc(100vh-12rem)] grid-cols-[250px_1fr] gap-4">
        {/* File List */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <FolderOpen className="h-4 w-4" /> Files ({files.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="h-full overflow-y-auto p-0">
            {loadingFiles ? (
              <p className="p-4 text-center text-sm text-slate-400">Loading...</p>
            ) : files.length === 0 ? (
              <p className="p-4 text-center text-sm text-slate-400">No memory files found</p>
            ) : (
              <div className="space-y-0.5 p-2">
                {files.map((file) => (
                  <button
                    key={file.path}
                    type="button"
                    onClick={() => loadFile(file.path)}
                    className={`flex w-full items-start gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${
                      selectedPath === file.path
                        ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
                        : "text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800"
                    }`}
                  >
                    <File className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{file.name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatBytes(file.size)} &middot; {new Date(file.modifiedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Editor */}
        <Card className="flex flex-col overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              {selectedPath ? (
                <>
                  <File className="h-4 w-4" />
                  {selectedFile?.name ?? selectedPath}
                  {isDirty && <Badge variant="outline" className="border-amber-500 text-[10px] text-amber-600">unsaved</Badge>}
                </>
              ) : (
                "Select a file"
              )}
            </CardTitle>
            {selectedPath && isDirty && (
              <button
                type="button"
                onClick={saveFile}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <Save className="h-3 w-3" /> {saving ? "Saving..." : "Save"}
              </button>
            )}
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            {loadingContent ? (
              <p className="p-4 text-center text-sm text-slate-400">Loading file...</p>
            ) : selectedPath ? (
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="h-full w-full resize-none border-0 bg-transparent p-4 font-mono text-sm leading-relaxed outline-none"
                spellCheck={false}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-slate-400">Select a file from the list to view or edit</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
